import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle, AlertCircle, Shield } from 'lucide-react';

const BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app/isfathena';

// ─────────────────────────────────────────────────────────────
//  AddProduct Page
//
//  Routes:
//    /addproduct           → Add new product
//    /addproduct?editId=5  → Edit existing product (id=5)
//
//  Access: Super Admin only  (role === 'super_admin')
// ─────────────────────────────────────────────────────────────
function AddProduct() {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();

  const editId = searchParams.get('editId');  // present when editing
  const isEdit = Boolean(editId);

  // ── Auth ────────────────────────────────────────────────────
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);

  // ── Form ────────────────────────────────────────────────────
  const [productName, setProductName] = useState('');
  const [loading,     setLoading]     = useState(false);
  const [fetchError,  setFetchError]  = useState('');
  const [submitError, setSubmitError] = useState('');
  const [success,     setSuccess]     = useState('');

  // ── Check auth on mount ──────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) { navigate('/login'); return; }
      const user = JSON.parse(raw);
      const isAdmin = user.role === 'super_admin' || user.email === 'info@easylearn.net.in';
      setIsSuperAdmin(isAdmin);
      setAuthChecked(true);
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  // ── If editing, pre-fill product name ───────────────────────
  useEffect(() => {
    if (!isEdit || !authChecked || !isSuperAdmin) return;

    setFetchError('');
    setLoading(true);

    fetch(`${BASE_URL}/product/list`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : (data.products || data.data || data.list || []);
        const found = list.find((p) => String(p.id) === String(editId));
        if (found) {
          setProductName(found.product);
        } else {
          setFetchError(`Product with ID ${editId} not found.`);
        }
      })
      .catch(() => setFetchError('Failed to load product details.'))
      .finally(() => setLoading(false));
  }, [isEdit, editId, authChecked, isSuperAdmin]);

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = productName.trim();
    if (!trimmed) { setSubmitError('Product name is required.'); return; }

    setLoading(true);
    setSubmitError('');
    setSuccess('');

    try {
      const fd = new FormData();
      let url;

      if (isEdit) {
        fd.append('id', editId);
        fd.append('product', trimmed);
        url = `${BASE_URL}/product/update`;
      } else {
        fd.append('product', trimmed);
        url = `${BASE_URL}/product/create`;
      }

      const res  = await fetch(url, { method: 'POST', body: fd });
      const data = await res.json();

      if (data.success) {
        setSuccess(isEdit ? '✅ Product updated successfully!' : '✅ Product created successfully!');
        if (!isEdit) setProductName('');
        // After 2 seconds, go back to reference page
        setTimeout(() => navigate('/standard'), 2000);
      } else {
        setSubmitError(data.message || `Failed to ${isEdit ? 'update' : 'create'} product.`);
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  RENDER — Auth not checked yet
  // ─────────────────────────────────────────────────────────────
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 border-4 border-gray-700 rounded-full" />
          <div className="absolute inset-0 border-4 border-t-orange-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  //  RENDER — Access Denied
  // ─────────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 border border-red-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Access Restricted</h1>
          <p className="text-gray-400 mb-6">Only Super Admins can manage products.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-all">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  //  RENDER — Main Form
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-20">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Back button */}
        <button
          onClick={() => navigate('/standard')}
          className="flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors group mb-8">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Back to Products</span>
        </button>

        {/* Card */}
        <div className="bg-gray-800/60 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 sm:p-8 shadow-2xl">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                {isEdit ? 'Edit Product' : 'Add New Product'}
              </h1>
              <p className="text-gray-400 text-sm">
                {isEdit ? `Updating product ID: ${editId}` : 'Create a new product in the system'}
              </p>
            </div>
          </div>

          {/* Fetch error (edit mode) */}
          {fetchError && (
            <div className="mb-5 p-4 bg-red-500/15 border border-red-500/40 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-300 text-sm">{fetchError}</p>
            </div>
          )}

          {/* Success banner */}
          {success && (
            <div className="mb-5 p-4 bg-green-500/15 border border-green-500/40 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-green-300 text-sm font-medium">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Product Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={productName}
                onChange={(e) => { setProductName(e.target.value); setSubmitError(''); }}
                placeholder="e.g. EasyLearn LMS Business"
                disabled={loading}
                className="w-full px-4 py-3 bg-gray-900/60 border border-gray-600 rounded-xl text-white
                           placeholder-gray-500 focus:outline-none focus:border-orange-500
                           focus:ring-2 focus:ring-orange-500/30 transition-all disabled:opacity-60"
              />
              {submitError && (
                <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {submitError}
                </p>
              )}
              <p className="text-gray-500 text-xs mt-2">
                This name will appear on the product card. Keep it clear and descriptive.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/standard')}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-white/20 text-gray-300 rounded-xl font-semibold
                           hover:bg-white/10 transition-all disabled:opacity-50 text-sm text-center">
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || Boolean(success)}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl
                           font-bold hover:from-orange-600 hover:to-orange-700 transition-all
                           disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    {isEdit ? 'Saving…' : 'Creating…'}
                  </>
                ) : isEdit ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </form>
        </div>

        {/* Info card */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <p className="text-blue-300 text-xs leading-relaxed">
            <span className="font-semibold">ℹ️ Note:</span> Products are the top-level containers.
            After creating a product, go back to the product list and open it to add modals inside it.
          </p>
        </div>

      </div>
    </div>
  );
}

export default AddProduct;